const EARTH_RADIUS_METERS = 6371000;

function toRadians(value) {
  return (Number(value) * Math.PI) / 180;
}

function isValidLatitude(value) {
  const number = Number(value);
  return Number.isFinite(number) && number >= -90 && number <= 90;
}

function isValidLongitude(value) {
  const number = Number(value);
  return Number.isFinite(number) && number >= -180 && number <= 180;
}

function toMillis(value) {
  if (value === null || value === undefined) return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value === "string") {
    const parsed = Date.parse(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  if (value instanceof Date) return value.getTime();
  if (typeof value?.toMillis === "function") return value.toMillis();
  if (typeof value?._seconds === "number") return value._seconds * 1000;
  return null;
}

function resolveNumber(source, fields) {
  for (const field of fields) {
    const value = Number(source?.[field]);
    if (Number.isFinite(value)) return value;
  }
  return null;
}

function firstFinite(values) {
  for (const value of values) {
    const number = Number(value);
    if (Number.isFinite(number)) return number;
  }
  return null;
}

function hasFiniteNumber(value) {
  return value !== null && value !== undefined && Number.isFinite(Number(value));
}

function normalizeRoutePoint(source, fallbackIndex = 0) {
  const position = source?.position || {};
  const snappedLat = resolveNumber(source, ["snappedLatitude"]);
  const snappedLng = resolveNumber(source, ["snappedLongitude"]);
  const rawLat = resolveNumber(source, ["rawLatitude"]);
  const rawLng = resolveNumber(source, ["rawLongitude"]);
  const sampleLat = resolveNumber(source, ["latitude", "lat"]) ?? Number(position.lat);
  const sampleLng = resolveNumber(source, ["longitude", "lng", "lon"]) ?? Number(position.lng);
  const hasSnappedCoordinatePair =
    snappedLat != null &&
    snappedLng != null &&
    isValidLatitude(snappedLat) &&
    isValidLongitude(snappedLng);
  const hasRawCoordinatePair =
    rawLat != null &&
    rawLng != null &&
    isValidLatitude(rawLat) &&
    isValidLongitude(rawLng);
  const hasSampleCoordinatePair =
    isValidLatitude(sampleLat) &&
    isValidLongitude(sampleLng);
  const lat = hasSnappedCoordinatePair
    ? snappedLat
    : hasSampleCoordinatePair
    ? sampleLat
    : hasRawCoordinatePair
    ? rawLat
    : null;
  const lng = hasSnappedCoordinatePair
    ? snappedLng
    : hasSampleCoordinatePair
    ? sampleLng
    : hasRawCoordinatePair
    ? rawLng
    : null;
  if (!isValidLatitude(lat) || !isValidLongitude(lng)) return null;
  if (Number(lat) === 0 && Number(lng) === 0) return null;

  const recordedAtMs = firstFinite([
    source?.recordedAtMs,
    toMillis(source?.recordedAt),
  ]);
  const sortAtMs = firstFinite([
    recordedAtMs,
    source?.sortAtMs,
    source?.createdAtMs,
    toMillis(source?.createdAt),
  ]);

  return {
    lat: Number(lat),
    lng: Number(lng),
    recordedAtMs,
    sortAtMs,
    sequenceNumber: resolveNumber(source, ["sequenceNumber", "seq"]),
    horizontalAccuracyM: resolveNumber(source, ["horizontalAccuracyM", "horizontalAccuracy", "accuracyM", "accuracy"]),
    snappedToRoad: hasSnappedCoordinatePair,
    sourceIndex: fallbackIndex,
  };
}

export function distanceMeters(left, right) {
  if (!left || !right) return null;
  const deltaLat = toRadians(right.lat - left.lat);
  const deltaLng = toRadians(right.lng - left.lng);
  const leftLat = toRadians(left.lat);
  const rightLat = toRadians(right.lat);
  const a =
    Math.sin(deltaLat / 2) ** 2 +
    Math.cos(leftLat) * Math.cos(rightLat) * Math.sin(deltaLng / 2) ** 2;
  return 2 * EARTH_RADIUS_METERS * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export function hasRouteJoinLongerThan(points = [], maxDistanceMeters) {
  const maxDistance = Number(maxDistanceMeters);
  if (!Array.isArray(points) || points.length < 2 || !Number.isFinite(maxDistance) || maxDistance <= 0) {
    return false;
  }

  for (let index = 1; index < points.length; index += 1) {
    const distance = distanceMeters(points[index - 1], points[index]);
    if (Number.isFinite(distance) && distance > maxDistance) return true;
  }

  return false;
}

export function routeDistanceMeters(points = []) {
  if (!Array.isArray(points) || points.length < 2) return 0;

  return points.reduce((sum, point, index) => {
    if (index === 0) return sum;
    const distance = distanceMeters(points[index - 1], point);
    return Number.isFinite(distance) ? sum + distance : sum;
  }, 0);
}

function projectRoutePoint(point, origin, scale) {
  return {
    x: (point.lng - origin.lng) * scale.lng,
    y: (point.lat - origin.lat) * scale.lat,
  };
}

function orientation(a, b, c) {
  const value = (b.y - a.y) * (c.x - b.x) - (b.x - a.x) * (c.y - b.y);
  if (Math.abs(value) < 0.000001) return 0;
  return value > 0 ? 1 : 2;
}

function isOnSegment(a, b, c) {
  return (
    b.x <= Math.max(a.x, c.x) + 0.000001 &&
    b.x + 0.000001 >= Math.min(a.x, c.x) &&
    b.y <= Math.max(a.y, c.y) + 0.000001 &&
    b.y + 0.000001 >= Math.min(a.y, c.y)
  );
}

function segmentsIntersect(a, b, c, d) {
  const o1 = orientation(a, b, c);
  const o2 = orientation(a, b, d);
  const o3 = orientation(c, d, a);
  const o4 = orientation(c, d, b);

  if (o1 !== o2 && o3 !== o4) return true;
  if (o1 === 0 && isOnSegment(a, c, b)) return true;
  if (o2 === 0 && isOnSegment(a, d, b)) return true;
  if (o3 === 0 && isOnSegment(c, a, d)) return true;
  if (o4 === 0 && isOnSegment(c, b, d)) return true;
  return false;
}

export function hasRouteSelfIntersection(points = [], options = {}) {
  const {
    minSegmentMeters = 20,
  } = options;
  const validPoints = Array.isArray(points) ? points.filter(Boolean) : [];
  if (validPoints.length < 4) return false;

  const origin = validPoints[0];
  const scale = metersPerDegreeAtLatitude(origin.lat);
  const projected = validPoints.map((point) => projectRoutePoint(point, origin, scale));

  for (let left = 0; left < validPoints.length - 1; left += 1) {
    const leftDistance = distanceMeters(validPoints[left], validPoints[left + 1]);
    if (Number.isFinite(leftDistance) && leftDistance < minSegmentMeters) continue;

    for (let right = left + 2; right < validPoints.length - 1; right += 1) {
      const rightDistance = distanceMeters(validPoints[right], validPoints[right + 1]);
      if (Number.isFinite(rightDistance) && rightDistance < minSegmentMeters) continue;

      if (segmentsIntersect(
        projected[left],
        projected[left + 1],
        projected[right],
        projected[right + 1]
      )) {
        return true;
      }
    }
  }

  return false;
}

export function splitRouteAtSelfIntersections(points = [], options = {}) {
  const {
    minSegmentMeters = 20,
  } = options;
  const validPoints = Array.isArray(points) ? points.filter(Boolean) : [];
  if (validPoints.length < 4) return validPoints.length >= 2 ? [validPoints] : [];

  const segments = [];
  let current = [];

  validPoints.forEach((point) => {
    if (current.length < 2) {
      current.push(point);
      return;
    }

    const candidate = [...current, point];
    if (hasRouteSelfIntersection(candidate, { minSegmentMeters })) {
      if (current.length >= 2) segments.push(current);
      current = [current.at(-1), point];
      return;
    }

    current.push(point);
  });

  if (current.length >= 2) segments.push(current);
  return segments;
}

export function isPlausibleRoutedPath(routePoints = [], sourcePoints = [], options = {}) {
  const {
    maxDistanceRatio = 4,
    maxExtraDistanceMeters = 5000,
    minIntersectionSegmentMeters = 20,
  } = options;
  const routed = Array.isArray(routePoints) ? routePoints.filter(Boolean) : [];
  const source = Array.isArray(sourcePoints) ? sourcePoints.filter(Boolean) : [];
  if (routed.length < 2 || source.length < 2) return false;
  if (hasRouteSelfIntersection(routed, { minSegmentMeters: minIntersectionSegmentMeters })) {
    return false;
  }

  const sourceDistance = routeDistanceMeters(source);
  const routedDistance = routeDistanceMeters(routed);
  if (!Number.isFinite(sourceDistance) || !Number.isFinite(routedDistance) || sourceDistance <= 0) {
    return false;
  }

  return routedDistance <= Math.max(sourceDistance * maxDistanceRatio, sourceDistance + maxExtraDistanceMeters);
}

export function splitRouteAtLongJoins(points = [], maxDistanceMeters) {
  const maxDistance = Number(maxDistanceMeters);
  if (!Array.isArray(points) || points.length < 2) return [];
  if (!Number.isFinite(maxDistance) || maxDistance <= 0) return [points];

  const segments = [];
  let current = [points[0]];

  for (let index = 1; index < points.length; index += 1) {
    const previous = current.at(-1);
    const point = points[index];
    const distance = distanceMeters(previous, point);

    if (Number.isFinite(distance) && distance > maxDistance) {
      if (current.length >= 2) segments.push(current);
      current = [point];
      continue;
    }

    current.push(point);
  }

  if (current.length >= 2) segments.push(current);
  return segments;
}

function metersPerDegreeAtLatitude(latitude) {
  const latRadians = toRadians(latitude);
  return {
    lat: 111320,
    lng: Math.max(1, 111320 * Math.cos(latRadians)),
  };
}

function pointLineDistanceMeters(point, start, end) {
  const scale = metersPerDegreeAtLatitude(point.lat);
  const px = (point.lng - start.lng) * scale.lng;
  const py = (point.lat - start.lat) * scale.lat;
  const ex = (end.lng - start.lng) * scale.lng;
  const ey = (end.lat - start.lat) * scale.lat;
  const lengthSq = ex * ex + ey * ey;
  if (!lengthSq) return distanceMeters(point, start) || 0;
  const t = Math.max(0, Math.min(1, (px * ex + py * ey) / lengthSq));
  const dx = px - t * ex;
  const dy = py - t * ey;
  return Math.sqrt(dx * dx + dy * dy);
}

function simplifyRoute(points, toleranceMeters) {
  if (points.length <= 2) return points;

  let maxDistance = 0;
  let index = 0;
  const start = points[0];
  const end = points[points.length - 1];

  for (let i = 1; i < points.length - 1; i += 1) {
    const distance = pointLineDistanceMeters(points[i], start, end);
    if (distance > maxDistance) {
      maxDistance = distance;
      index = i;
    }
  }

  if (maxDistance <= toleranceMeters) return [start, end];

  const left = simplifyRoute(points.slice(0, index + 1), toleranceMeters);
  const right = simplifyRoute(points.slice(index), toleranceMeters);
  return [...left.slice(0, -1), ...right];
}

function removeDetourSpikes(points, options = {}) {
  const {
    maxDetourRatio = 2.4,
    minDetourMeters = 350,
    minOffLineMeters = 120,
    passes = 4,
  } = options;
  let current = [...points];

  for (let pass = 0; pass < passes && current.length > 3; pass += 1) {
    const next = [current[0]];
    let removed = 0;

    for (let index = 1; index < current.length - 1; index += 1) {
      const previous = next.at(-1);
      const point = current[index];
      const following = current[index + 1];
      const direct = distanceMeters(previous, following);
      const via =
        (distanceMeters(previous, point) || 0) +
        (distanceMeters(point, following) || 0);
      const offLine = pointLineDistanceMeters(point, previous, following);
      const detour = via - (direct || 0);
      const ratio = direct && direct > 0 ? via / direct : 1;

      if (
        Number.isFinite(direct) &&
        Number.isFinite(via) &&
        Number.isFinite(offLine) &&
        detour >= minDetourMeters &&
        offLine >= minOffLineMeters &&
        ratio >= maxDetourRatio
      ) {
        removed += 1;
        continue;
      }

      next.push(point);
    }

    next.push(current.at(-1));
    current = next;
    if (!removed) break;
  }

  return current;
}

function removeLongSparseFanSegments(points, options = {}) {
  const {
    maxSegmentMeters = 3500,
    maxSegmentWithoutTimeMeters = 1800,
  } = options;
  if (points.length <= 2) return points;

  const filtered = [points[0]];
  for (let index = 1; index < points.length; index += 1) {
    const previous = filtered.at(-1);
    const point = points[index];
    const distance = distanceMeters(previous, point);
    const hasTimes =
      Number.isFinite(Number(previous.recordedAtMs)) &&
      Number.isFinite(Number(point.recordedAtMs));

    if (
      Number.isFinite(distance) &&
      ((hasTimes && distance > maxSegmentMeters) || (!hasTimes && distance > maxSegmentWithoutTimeMeters)) &&
      index < points.length - 1
    ) {
      continue;
    }

    filtered.push(point);
  }

  return filtered.length >= 2 ? filtered : points.slice(0, 2);
}

function splitLongDisplaySegments(points, maxDrawSegmentMeters, maxSnappedDrawSegmentMeters = null) {
  if (!Number.isFinite(Number(maxDrawSegmentMeters)) || Number(maxDrawSegmentMeters) <= 0) {
    return [points];
  }
  if (!Array.isArray(points) || points.length < 2) return [];

  const segments = [];
  let current = [points[0]];

  for (let index = 1; index < points.length; index += 1) {
    const previous = current.at(-1);
    const point = points[index];
    const distance = distanceMeters(previous, point);
    const drawLimit =
      previous?.snappedToRoad &&
      point?.snappedToRoad &&
      Number.isFinite(Number(maxSnappedDrawSegmentMeters)) &&
      Number(maxSnappedDrawSegmentMeters) > 0
        ? Number(maxSnappedDrawSegmentMeters)
        : Number(maxDrawSegmentMeters);

    if (Number.isFinite(distance) && distance > drawLimit) {
      if (current.length >= 2) segments.push(current);
      current = [point];
      continue;
    }

    current.push(point);
  }

  if (current.length >= 2) segments.push(current);
  return segments;
}

export function buildCleanRoutePath(samples = [], options = {}) {
  return buildCleanRouteSegments(samples, options).flat();
}

export function buildSolidRoadRouteSegments(samples = [], options = {}) {
  return buildCleanRouteSegments(samples, {
    includeLastPoint: true,
    maxJumpMeters: 1200,
    maxJumpWithoutTimeMeters: 1200,
    maxRoutePoints: 700,
    minPointDistanceMeters: 6,
    simplifyToleranceMeters: 5,
    ...options,
    maxDrawSegmentMeters: null,
    maxSnappedDrawSegmentMeters: null,
  });
}

export function selectPrimaryRouteSegments(segments = []) {
  const validSegments = (Array.isArray(segments) ? segments : [])
    .filter((segment) => Array.isArray(segment) && segment.length >= 2);
  if (validSegments.length <= 1) return validSegments;

  const longestSegment = validSegments.reduce((selected, segment) => {
    if (!selected) return segment;
    return segment.length > selected.length ? segment : selected;
  }, null);

  return longestSegment ? [longestSegment] : [];
}

export function buildCleanRouteSegments(samples = [], options = {}) {
  const {
    maxAccuracyMeters = 80,
    minPointDistanceMeters = 8,
    maxJumpSpeedKph = 160,
    maxJumpMeters = 800,
    maxJumpWithoutTimeMeters = 350,
    simplifyToleranceMeters = 6,
    maxRoutePoints = 600,
    maxDrawSegmentMeters = null,
    maxSnappedDrawSegmentMeters = null,
    includeLastPoint = true,
    requireReliableOrder = true,
  } = options;

  const routePoints = samples
    .map(normalizeRoutePoint)
    .filter(Boolean)
    .filter((point) => point.horizontalAccuracyM == null || point.horizontalAccuracyM <= maxAccuracyMeters);
  const hasSequencedRoutePoints = routePoints.some((point) => hasFiniteNumber(point.sequenceNumber));

  const normalized = routePoints
    .filter((point) => {
      if (hasSequencedRoutePoints) return hasFiniteNumber(point.sequenceNumber);
      if (!requireReliableOrder) return true;
      return hasFiniteNumber(point.recordedAtMs) || hasFiniteNumber(point.sequenceNumber);
    })
    .sort((a, b) => {
      const leftSequence = Number(a.sequenceNumber);
      const rightSequence = Number(b.sequenceNumber);
      if (hasFiniteNumber(a.sequenceNumber) && hasFiniteNumber(b.sequenceNumber) && leftSequence !== rightSequence) {
        return leftSequence - rightSequence;
      }
      const leftTime = Number(a.recordedAtMs);
      const rightTime = Number(b.recordedAtMs);
      if (hasFiniteNumber(a.recordedAtMs) && hasFiniteNumber(b.recordedAtMs) && leftTime !== rightTime) {
        return leftTime - rightTime;
      }
      if (!requireReliableOrder) {
        const leftSortTime = Number(a.sortAtMs);
        const rightSortTime = Number(b.sortAtMs);
        if (hasFiniteNumber(a.sortAtMs) && hasFiniteNumber(b.sortAtMs) && leftSortTime !== rightSortTime) {
          return leftSortTime - rightSortTime;
        }
      }
      return a.sourceIndex - b.sourceIndex;
    });

  if (normalized.length <= 1) {
    return normalized.length ? [normalized.map(({ lat, lng }) => ({ lat, lng }))] : [];
  }

  const cleanedSegments = [];
  let currentSegment = [];
  for (const point of normalized) {
    const previous = currentSegment.at(-1);
    if (!previous) {
      currentSegment.push(point);
      continue;
    }

    const distance = distanceMeters(previous, point);
    if (!Number.isFinite(distance)) continue;
    if (distance < minPointDistanceMeters) continue;

    const timeDeltaSeconds =
      Number.isFinite(Number(previous.recordedAtMs)) && Number.isFinite(Number(point.recordedAtMs))
        ? Math.abs(Number(point.recordedAtMs) - Number(previous.recordedAtMs)) / 1000
        : null;
    const impliedSpeedKph =
      timeDeltaSeconds && timeDeltaSeconds >= 1 ? (distance / timeDeltaSeconds) * 3.6 : null;
    const isImpossibleTimedJump = impliedSpeedKph != null && impliedSpeedKph > maxJumpSpeedKph;
    const isLongJump = timeDeltaSeconds != null
      ? distance > maxJumpMeters
      : distance > maxJumpWithoutTimeMeters;
    if (isImpossibleTimedJump || isLongJump) {
      if (currentSegment.length >= 2) cleanedSegments.push(currentSegment);
      currentSegment = [point];
      continue;
    }

    currentSegment.push(point);
  }
  if (currentSegment.length >= 2) cleanedSegments.push(currentSegment);

  const lastPoint = normalized.at(-1);
  const lastSegment = cleanedSegments.at(-1);
  if (includeLastPoint && lastPoint && lastSegment?.length && lastSegment.at(-1) !== lastPoint) {
    const previous = lastSegment.at(-1);
    const distanceFromLast = distanceMeters(previous, lastPoint);
    const timeDeltaSeconds =
      Number.isFinite(Number(previous.recordedAtMs)) && Number.isFinite(Number(lastPoint.recordedAtMs))
        ? Math.abs(Number(lastPoint.recordedAtMs) - Number(previous.recordedAtMs)) / 1000
        : null;
    const impliedSpeedKph =
      timeDeltaSeconds && timeDeltaSeconds >= 1 ? (distanceFromLast / timeDeltaSeconds) * 3.6 : null;
    const isImpossibleTimedJump = impliedSpeedKph != null && impliedSpeedKph > maxJumpSpeedKph;
    const isLongJump = timeDeltaSeconds != null
      ? distanceFromLast > maxJumpMeters
      : distanceFromLast > maxJumpWithoutTimeMeters;

    if (
      Number.isFinite(distanceFromLast) &&
      distanceFromLast >= minPointDistanceMeters &&
      !isImpossibleTimedJump &&
      !isLongJump
    ) {
      lastSegment.push(lastPoint);
    }
  }

  const totalPoints = cleanedSegments.reduce((sum, segment) => sum + segment.length, 0) || 1;
  return cleanedSegments
    .map((segment) => {
      const despiked = removeLongSparseFanSegments(removeDetourSpikes(segment, {
        maxDetourRatio: 1.9,
        minDetourMeters: 120,
        minOffLineMeters: 45,
        passes: 6,
      }), {
        maxSegmentMeters: maxJumpMeters,
        maxSegmentWithoutTimeMeters: maxJumpWithoutTimeMeters,
      });
      const simplified = simplifyRoute(despiked, simplifyToleranceMeters);
      const segmentBudget = Math.max(2, Math.ceil(maxRoutePoints * (segment.length / totalPoints)));
      const stride = Math.max(1, Math.ceil(simplified.length / segmentBudget));
      return simplified
        .filter((_, index) => index === 0 || index === simplified.length - 1 || index % stride === 0)
        .map((point) => ({
          lat: Number(point.lat.toFixed(6)),
          lng: Number(point.lng.toFixed(6)),
          snappedToRoad: Boolean(point.snappedToRoad),
        }));
    })
    .flatMap((segment) => {
      if (!Number.isFinite(Number(maxDrawSegmentMeters))) return [segment];
      return splitLongDisplaySegments(segment, Number(maxDrawSegmentMeters), maxSnappedDrawSegmentMeters);
    })
    .filter((segment) => segment.length >= 2)
    .map((segment) =>
      segment.map(({ lat, lng }) => ({
        lat,
        lng,
      }))
    );
}
